import { ColorEnum } from '@/constants/color'
import { useEnabledButtonState } from '@/features/ui/buttons/hooks/useEnabledButtonState'
import { isInteractiveButtonState } from '@/features/ui/buttons/utils'
import Typography from '@/features/ui/typography'
import { hexToRgba } from '@/utils/hexToRgba'
import { FC, ReactNode } from 'react'
import { Pressable, StyleProp, TextStyle, ViewStyle } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

export interface OptionProps {
    children: string
    value: string
    onPress?: (value: string) => void
    FrontIcon?: ReactNode
    textStyle?: StyleProp<TextStyle>
    style?: StyleProp<ViewStyle>
}

const Option: FC<OptionProps> = ({ children, value, onPress, FrontIcon, textStyle, style }) => {
    const { state, eventHandlers } = useEnabledButtonState()

    const { styles } = useStyles(stylesheet, {
        state: isInteractiveButtonState(state) ? state : undefined,
    })

    const handlePress = () => {
        if (onPress) onPress(value)
    }

    return (
        <Pressable {...eventHandlers} onPress={handlePress} style={[styles.container, style]}>
            {FrontIcon}
            <Typography.Description
                font="medium"
                numberOfLines={1}
                style={[styles.text, textStyle]}
            >
                {children}
            </Typography.Description>
        </Pressable>
    )
}

const stylesheet = createStyleSheet({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        maxWidth: '100%',
        paddingHorizontal: 16,
        gap: 8,
        variants: {
            state: {
                hovered: {
                    backgroundColor: hexToRgba(ColorEnum.JET_900, 0.0625),
                },
                pressed: {
                    backgroundColor: hexToRgba(ColorEnum.JET_900, 0.25),
                },
            },
        },
    },
    text: {
        color: ColorEnum.JET_900,
        lineHeight: 36,
    },
})

Option.displayName = 'Select.Option'

export default Option
